import { Link } from "react-router";
import { useState, useEffect } from "react";
import {
  BarChart,
  History,
} from "lucide-react";
import { Card, CardContent } from "@nosible/shared/components/ui/card";
import { Button } from "@nosible/shared/components/ui/button";
import { trpc } from "@/account/lib/trpc";
import { useSession } from "@/account/lib/auth-client";

interface SubCurrentUsageProps {
  subscriptionId: string;
}

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return "just now";
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes} ${diffInMinutes === 1 ? 'min' : 'mins'} ago`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
}

function formatTimestamp(date: Date): string {
  const d = date.getDate().toString().padStart(2, '0');
  const m = (date.getMonth() + 1).toString().padStart(2, '0');
  const y = date.getFullYear();
  const h = date.getHours().toString().padStart(2, '0');
  const min = date.getMinutes().toString().padStart(2, '0');
  return `${d}/${m}/${y} ${h}h${min}`;
}

export function SubCurrentUsage({ subscriptionId }: SubCurrentUsageProps) {
  const { data: session } = useSession();

  const { data: currentUsage } = trpc.subscription.usage.currentUsage.useQuery(
    { subscriptionId },
    { enabled: !!session && !!subscriptionId }
  );

  const [relativeTime, setRelativeTime] = useState<string>("");

  useEffect(() => {
    if (currentUsage?.usage?.timestamp) {
      const date = new Date(currentUsage.usage.timestamp);
      setRelativeTime(formatRelativeTime(date));

      const interval = setInterval(() => {
        setRelativeTime(formatRelativeTime(date));
      }, 30000); // Update every 30 seconds

      return () => clearInterval(interval);
    }
  }, [currentUsage?.usage?.timestamp]);

  const { data: pastUsage } = trpc.subscription.usage.pastUsage.useQuery(
    { subscriptionId },
    { enabled: !!session && !!subscriptionId }
  );

  return (
    <section className="account-reference-section account-usage-card" aria-labelledby="current-period-heading">
      <div className="subscription-section-heading" id="current-period-heading">
        <span>01 CURRENT PERIOD</span>
        <em>
          {currentUsage?.usage?.splits
            ? `${currentUsage.usage.splits.reduce((total, entry) => total + entry.used, 0).toLocaleString()} OF ${currentUsage.usage.splits.reduce((total, entry) => total + entry.limit, 0).toLocaleString()}`
            : "—"}
        </em>
      </div>
      <Card>
        <CardContent className="p-0">
          <div className="subscription-usage-list">
          {(!currentUsage || !currentUsage.usage || !currentUsage.usage.splits || currentUsage.usage.splits.length === 0) ? (
            <div className="text-center py-8">
              <div className="text-chromium-mist mb-2">No Usage Yet</div>
              <p className="text-sm text-chromium-mist/70">
                Start making API requests to see your usage statistics here.
              </p>
            </div>
          ) : (
            currentUsage.usage.splits.map((usageEntry: { type: string; limit: number; used: number }) => {
              const percentage = usageEntry.limit > 0 ? (usageEntry.used / usageEntry.limit) * 100 : 0;
              const remaining = usageEntry.limit - usageEntry.used;

              return (
                <div key={usageEntry.type} className="subscription-usage-row">
                  <div className="subscription-usage-row__label">
                    <strong>{usageEntry.type.charAt(0).toUpperCase() + usageEntry.type.slice(1)} requests</strong>
                    <span>{percentage < 1 ? "<1" : Math.round(percentage)}% USED</span>
                  </div>
                  <div className="subscription-usage-row__track" aria-label={`${usageEntry.type} usage ${Math.round(percentage)} percent`}>
                    <div className="subscription-usage-row__fill" style={{ width: `${Math.min(percentage, 100)}%` }} />
                  </div>
                  <div className="subscription-usage-row__numbers">
                    <div><strong>{usageEntry.used.toLocaleString()}</strong> <span>/ {usageEntry.limit.toLocaleString()}</span></div>
                    <small>{remaining.toLocaleString()} REMAINING</small>
                  </div>
                </div>
              );
            })
          )}
          </div>
        </CardContent>
      </Card>
      {currentUsage?.id && (
        <div className="subscription-usage-actions">
          <Button variant="outline" size="sm" asChild>
            <Link to={`/subscriptions/${subscriptionId}/period/${currentUsage.id}`}>
              <BarChart className="h-4 w-4 mr-1" />
              View Details
            </Link>
          </Button>
          {pastUsage && pastUsage.length > 1 && (
            <Button variant="outline" size="sm" asChild>
              <Link to={`/subscriptions/${subscriptionId}/periods`}>
                <History className="h-4 w-4 mr-1" />
                All Periods
              </Link>
            </Button>
          )}
          {currentUsage.usage?.timestamp && (
            <span className="subscription-usage-actions__updated">
              UPDATED {formatTimestamp(new Date(currentUsage.usage.timestamp))} ({relativeTime})
            </span>
          )}
        </div>
      )}
    </section>
  );
}
